import z from "zod";
export const ZPrincipalStr = z.string();
export const ZICRC1Subaccount = z.instanceof(Uint8Array);
export const ZOrigin = z.string().url();
export const ZTimestamp = z.number().nonnegative();
export const ZBlob = z.instanceof(ArrayBuffer).or(z.instanceof(Uint8Array));
export const ZIdentityId = z.number().int().nonnegative();
export const ZAccountId = z.number().int().nonnegative();
export const ZSession = z.object({
    /** chosen user identity ID (unique for each website) */
    identityId: ZIdentityId,
    /** an origin used for key deriviation (actual or linked) */
    deriviationOrigin: ZOrigin,
    /** logged in timestamp */
    timestampMs: ZTimestamp,
});
export const ZMask = z.object({ pseudonym: z.string().nonempty(), principal: ZPrincipalStr });
export const ZOriginData = z.object({
    /** identities (and their pseudonyms) a user has on this website */
    masks: z.record(z.string().nonempty(), z.optional(ZMask)),
    /** which websites shared the user's identity with this website */
    linksFrom: z.record(ZOrigin, z.optional(z.literal(true))),
    /** to which websites the user shared their identities from this website */
    linksTo: z.record(ZOrigin, z.optional(z.literal(true))),
    /** session object, exists if the user is logged in */
    currentSession: z.optional(ZSession),
});
export const ZOriginDataExternal = z.object({
    masks: z.array(ZMask),
    linksFrom: z.array(ZOrigin),
    linksTo: z.array(ZOrigin),
    currentSession: z.optional(ZSession),
});
export const ZStatistics = z.object({
    /** when was the last time the user sent the stats to the server */
    lastResetTimestamp: z.number().nonnegative(),
    /** how many activities were performed in any production environment by each activity type */
    prod: z.object({
        masks_created: z.number().nonnegative(),
        signatures_produced: z.number().nonnegative(),
        icrc1_accounts_created: z.number().nonnegative(),
        icrc1_sent: z.object({
            ICP: z.bigint().nonnegative(),
            ckBTC: z.bigint().nonnegative(),
            ckETH: z.bigint().nonnegative(),
            CHAT: z.bigint().nonnegative(),
            SONIC: z.bigint().nonnegative(),
            SNS1: z.bigint().nonnegative(),
            OGY: z.bigint().nonnegative(),
            MOD: z.bigint().nonnegative(),
            GHOST: z.bigint().nonnegative(),
            KINIC: z.bigint().nonnegative(),
            HOT: z.bigint().nonnegative(),
            CAT: z.bigint().nonnegative(),
        }),
        origins_linked: z.number().nonnegative(),
        origins_unlinked: z.number().nonnegative(),
    }),
});
export var EStatisticsKind;
(function (EStatisticsKind) {
    EStatisticsKind[EStatisticsKind["MasksCreated"] = 0] = "MasksCreated";
    EStatisticsKind[EStatisticsKind["SignaturesProduced"] = 1] = "SignaturesProduced";
    EStatisticsKind[EStatisticsKind["OriginsLinked"] = 2] = "OriginsLinked";
    EStatisticsKind[EStatisticsKind["OriginsUnlinked"] = 3] = "OriginsUnlinked";
    EStatisticsKind[EStatisticsKind["Icrc1AccountsCreated"] = 4] = "Icrc1AccountsCreated";
    EStatisticsKind[EStatisticsKind["Icrc1Sent"] = 5] = "Icrc1Sent";
})(EStatisticsKind || (EStatisticsKind = {}));
export const ZAssetData = z.object({
    accounts: z.record(z.string().nonempty(), z.string().nonempty()),
});
export const ZAssetDataExternal = z.object({
    accounts: z.array(z.string().nonempty()),
});
export const ZState = z.object({
    /** version of the state, for future migrations */
    version: z.number().nonnegative(),
    /** user data on each origin */
    originData: z.record(ZOrigin, z.optional(ZOriginData)),
    /** accounts for each asset */
    assetData: z.record(ZPrincipalStr, z.optional(ZAssetData)),
    /** anonymous usage stats */
    statistics: ZStatistics,
});
export const ZSnapRPCRequest = z.object({
    method: z.string(),
    params: z.object({
        body: z.string(),
    }),
});
// ----------- IDENTITY PROTOCOL RELATED TYPES ---------
export const ZIdentityGetLoginOptionsRequest = z.object({
    forOrigin: ZOrigin,
});
export const ZIdentityGetLoginOptionsResponse = z.array(z.tuple([ZOrigin, z.array(ZMask)]));
export const ZIdentityAddRequest = z.object({
    toOrigin: ZOrigin,
});
export const ZIdentityLoginRequest = z.object({
    /** Origin of the website to log in to */
    toOrigin: ZOrigin,
    /** Identity (mask) id of the user to use */
    withIdentityId: ZIdentityId,
    /** Linked origin, if the user wants to use it to log in */
    withLinkedOrigin: z.optional(ZOrigin),
});
export const ZIdentitySignRequest = z.object({
    challenge: ZBlob,
    salt: z.instanceof(Uint8Array),
});
export const ZIdentityGetPublicKeyRequest = z.object({
    salt: z.instanceof(Uint8Array),
});
export const ZIdentityLinkRequest = z.object({
    withOrigin: ZOrigin,
});
export const ZIdentityUnlinkRequest = z.object({
    withOrigin: ZOrigin,
});
export const ZIdentityEditPseudonymRequest = z.object({
    origin: ZOrigin,
    identityId: ZIdentityId,
    newPseudonym: z.string().nonempty(),
});
export const ZIdentityStopSessionRequest = z.object({
    origin: ZOrigin,
});
export const ZIdentityUnlinkOneRequest = z.object({
    origin: ZOrigin,
    withOrigin: ZOrigin,
});
export const ZIdentityUnlinkAllRequest = z.object({
    origin: ZOrigin,
});
// ----------- STATE PROTOCOL RELATED TYPES -------------
export const ZStateGetAllOriginDataRequest = z.object({ origins: z.optional(z.array(ZOrigin)) });
export const ZStateGetAllOriginDataResponse = z.record(ZOrigin, z.optional(ZOriginDataExternal));
export const ZStateGetAllAssetDataRequest = z.object({ assetIds: z.optional(z.array(ZPrincipalStr)) });
export const ZStateGetAllAssetDataResponse = z.record(ZPrincipalStr, z.optional(ZAssetDataExternal));
// ----------- ICRC1 PROTOCOL RELATED TYPES -------------
export const ZICRC1Account = z.object({
    owner: ZPrincipalStr,
    subaccount: z.optional(ZICRC1Subaccount),
});
export const ZICRC1TransferRequest = z.object({
    canisterId: ZPrincipalStr,
    to: ZICRC1Account,
    amount: z.bigint(),
    memo: z.optional(z.instanceof(Uint8Array)),
    createdAt: z.optional(z.bigint()),
});
export const ZShowICRC1TransferConfirmRequest = z.object({
    requestOrigin: ZOrigin,
    from: ZPrincipalStr,
    to: ZICRC1Account,
    totalAmountStr: z.string().nonempty(),
    totalAmount: z.bigint().nonnegative(),
    ticker: z.string().nonempty(),
});
export const ZICRC1AddAssetRequest = z.object({
    assets: z.array(z.object({
        assetId: ZPrincipalStr,
        name: z.optional(z.string()),
        symbol: z.optional(z.string()),
    })),
});
export const ZICRC1AddAssetAccountRequest = z.object({
    assetId: ZPrincipalStr,
    name: z.string(),
    symbol: z.string(),
});
export const ZICRC1EditAssetAccountRequest = z.object({
    assetId: ZPrincipalStr,
    accountId: ZAccountId,
    newName: z.string().nonempty(),
});
// ---------- MESSAGE TYPES ------------------------------
export const ZMsgDomain = z.literal("msq");
export const ZRequestReceivedMsg = z.object({
    domain: ZMsgDomain,
    type: z.literal("request_received"),
});
export const ZLoginRequestMsg = z.object({
    domain: ZMsgDomain,
    origin: z.string().url(),
    type: z.literal("login_request"),
});
export const ZLoginResultMsg = z.object({
    domain: ZMsgDomain,
    type: z.literal("login_result"),
    result: z.boolean(),
});
export const ZICRC1TransferRequestMsg = z.object({
    domain: ZMsgDomain,
    origin: z.string().url(),
    type: z.literal("transfer_icrc1_request"),
    request: ZICRC1TransferRequest,
});
export const ZICRC1TransferResultMsg = z.object({
    domain: ZMsgDomain,
    type: z.literal("transfer_icrc1_result"),
    result: z.optional(z.bigint()),
});
